import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PaperPlaneRight, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openChatSocket } from "../lib/chatSocket";

/**
 * Sprint G · Batch 5 — Bandeja de mensajes del taller.
 *
 * Flujo:
 *  - GET /api/chats/conversations-as-org → lista de conversaciones (ordenada
 *    por último mensaje, con unread count por cliente).
 *  - Al seleccionar una conversación: GET /api/chats/history-as-org y abre
 *    WebSocket kind=user contra la organización para recibir mensajes nuevos
 *    en tiempo real desde cualquier cliente.
 *  - Enviar: por WS si está abierto, REST como fallback.
 */
export default function OrganizationChats() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [convs, setConvs] = useState(null);   // null = loading, [] = empty
  const [selected, setSelected] = useState(null); // {customer_account_id, customer_id, name}
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const scrollerRef = useRef(null);
  const socketRef = useRef(null);

  const loadConversationsRaw = async () => {
    try {
      const { data } = await api.get("/chats/conversations-as-org");
      setConvs(data || []);
    } catch { setConvs([]); }
  };
  // Sprint H · debounce 300 ms para evitar bursts en ráfagas de mensajes.
  const debounceTimerRef = useRef(null);
  const loadConversations = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      loadConversationsRaw();
    }, 300);
  };

  useEffect(() => { loadConversationsRaw(); }, []);

  // Sprint H · Batch 8b — preselección por query string desde Calendar
  // (`/chats?customer_id=XXX` o `?customer_account_id=YYY`).
  useEffect(() => {
    if (!convs || convs.length === 0 || selected) return;
    const cid = searchParams.get("customer_id");
    const cacc = searchParams.get("customer_account_id");
    if (!cid && !cacc) return;
    const match = convs.find((c) =>
      (cacc && c.customer_account_id === cacc) ||
      (cid && c.customer_id === cid),
    );
    if (match) setSelected(match);
  }, [convs, searchParams, selected]);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
    }, 60);
  };

  const loadHistory = async (conv) => {
    try {
      const params = new URLSearchParams();
      if (conv.customer_account_id) params.set("customer_account_id", conv.customer_account_id);
      else if (conv.customer_id) params.set("customer_id", conv.customer_id);
      const { data } = await api.get(`/chats/history-as-org?${params.toString()}`);
      setMessages(data || []);
      scrollToBottom();
      // tras leer, refresca la lista (la API marca como leídos los mensajes)
      loadConversationsRaw();
    } catch { setMessages([]); }
  };

  // Abre/cierra WS al cambiar de conversación (solo si tiene customer_account_id,
  // los anónimos `customer_id` siguen usando REST).
  useEffect(() => {
    if (socketRef.current) { socketRef.current.close(); socketRef.current = null; setWsOpen(false); }
    if (!selected || !user?.organization_id) return;
    loadHistory(selected);

    if (!selected.customer_account_id) return; // sólo WS para CustomerAccounts logueadas
    const conn = openChatSocket({
      kind: "user",
      organizationId: user.organization_id,
      toCustomerAccountId: selected.customer_account_id,
      onOpen: () => setWsOpen(true),
      onClose: () => setWsOpen(false),
      onMessage: (msg) => {
        if ((msg?.type === "message" || msg?.type === "ack") && msg.data) {
          // sólo mensajes de la conversación activa
          if (msg.data.customer_account_id !== selected.customer_account_id) {
            // Pertenece a otra conversación → refresca la lista
            loadConversations();
            return;
          }
          setMessages((arr) => (arr.some((m) => m.id === msg.data.id) ? arr : [...arr, msg.data]));
          scrollToBottom();
        }
      },
    });
    socketRef.current = conn;
    return () => { conn.close(); socketRef.current = null; };
  // eslint-disable-next-line
  }, [selected?.customer_account_id, selected?.customer_id, user?.organization_id]);

  const totalUnread = useMemo(
    () => (convs || []).reduce((s, c) => s + (c.unread || 0), 0),
    [convs],
  );

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !selected) return;
    const body = text.trim();
    setSending(true);
    try {
      const conn = socketRef.current;
      if (conn && conn.isOpen() && selected.customer_account_id) {
        conn.send({ message_text: body, client_id: `u-${Date.now()}` });
        setText("");
      } else {
        const payload = {
          organization_id: user.organization_id,
          message_text: body,
          ...(selected.customer_account_id
            ? { customer_account_id: selected.customer_account_id }
            : { customer_id: selected.customer_id }),
        };
        const { data } = await api.post("/chats/send-from-organization", payload);
        setMessages((arr) => (arr.some((m) => m.id === data.message?.id) ? arr : [...arr, data.message]));
        setText("");
        scrollToBottom();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el mensaje");
    } finally { setSending(false); }
  };

  if (convs === null) {
    return <div className="text-center text-sm text-[var(--ag-text-dim)] py-12" data-testid="org-chats-loading">Cargando…</div>;
  }
  if (!convs.length) {
    return (
      <div className="ag-card p-8 text-center text-sm text-[var(--ag-text-dim)]" data-testid="org-chats-empty">
        <ChatCircleDots size={32} weight="duotone" className="mx-auto mb-2 opacity-50" />
        Aún no hay conversaciones. Cuando un cliente reserve o te escriba desde la PWA, aparecerá aquí.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="org-chats-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Comunicación cliente</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Bandeja de mensajes</h1>
        </div>
        {totalUnread > 0 && (
          <span className="text-xs px-2 py-1 rounded-sm bg-[#FFB703] text-black font-bold" data-testid="org-chats-total-unread">
            {totalUnread} sin leer
          </span>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-[600px]">
        {/* Lista de conversaciones */}
        <aside className="ag-card overflow-y-auto" data-testid="org-chats-list">
          {convs.map((c) => {
            const key = c.customer_account_id || c.customer_id;
            const active = selected && (selected.customer_account_id === c.customer_account_id && selected.customer_id === c.customer_id);
            return (
              <button
                key={key}
                onClick={() => setSelected(c)}
                className={`w-full text-left p-3 border-b border-[var(--ag-border)] flex items-center gap-2 hover:bg-[var(--ag-midnight)]/30 transition-colors ${active ? "bg-[var(--ag-midnight)]/40" : ""}`}
                data-testid={`org-chat-conv-${key}`}
              >
                <div className="w-9 h-9 rounded-sm bg-[var(--ag-gold)] text-white grid place-items-center text-xs font-bold shrink-0">
                  {(c.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold text-sm truncate">{c.name || "Cliente"}</div>
                    {c.unread > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#FFB703] text-black font-bold" data-testid={`org-chat-unread-${key}`}>
                        {c.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--ag-text-dim)] truncate">
                    {c.last_sender === "organization" ? "Tú: " : ""}{c.last_message || "Sin mensajes"}
                  </div>
                </div>
              </button>
            );
          })}
        </aside>

        {/* Conversación activa */}
        <section className="md:col-span-2 ag-card flex flex-col" data-testid="org-chat-conversation">
          {!selected ? (
            <div className="flex-1 grid place-items-center text-sm text-[var(--ag-text-dim)]">
              Selecciona una conversación para empezar.
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-[var(--ag-border)] text-sm font-semibold flex items-center justify-between" data-testid="org-chat-header">
                <span>{selected.name || "Cliente"}</span>
                {selected.customer_account_id && (
                  <span className={`text-[10px] uppercase tracking-wider ${wsOpen ? "text-emerald-400" : "text-[var(--ag-text-dim)]"}`} data-testid="org-chat-ws-status">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${wsOpen ? "bg-emerald-400" : "bg-gray-400"}`} />
                    {wsOpen ? "En vivo" : "Conectando…"}
                  </span>
                )}
              </div>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[var(--ag-midnight)]/30" data-testid="org-chat-messages">
                {messages.length === 0 && (
                  <div className="text-center text-xs text-[var(--ag-text-dim)] py-8">Sin mensajes todavía.</div>
                )}
                {messages.map((m) => {
                  const mine = m.sender_type === "organization";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`org-chat-msg-${m.id}`}>
                      <div className={`max-w-[80%] rounded-md px-3 py-2 text-sm ${mine ? "bg-[var(--ag-gold)] text-[var(--ag-text)]" : "bg-[var(--ag-midnight)] text-[var(--ag-text)] border border-[var(--ag-border)]"}`}>
                        {m.message_text}
                        <div className={`text-[9px] mt-1 ${mine ? "text-blue-100" : "text-[var(--ag-text-dim)]"}`}>
                          {new Date(m.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form onSubmit={send} className="p-2 border-t border-[var(--ag-border)] flex gap-2" data-testid="org-chat-form">
                <Input value={text} onChange={(e)=>setText(e.target.value)}
                  placeholder="Escribe tu respuesta al cliente…"
                  className="rounded-sm bg-transparent" disabled={sending} data-testid="org-chat-input" />
                <Button type="submit" disabled={sending || !text.trim()}
                  className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="org-chat-send">
                  <PaperPlaneRight size={14} weight="fill" />
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
